// src/components/attendance/AttendanceView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { attendanceBySession } from "../../api/attendance";
import { listEnrollments } from "../../api/enrollments";
import { getWalkInsBySession } from "../../api/sessionParticipants";
import { formatDateTime } from "../../utils/date";
import { formatDate, formatDateTime as formatDateTimeRegional, formatTime } from "../../utils/regionalFormat";
import { combineSessionDateWithLocalTime } from "../../utils/sessionDateTimeDisplay";
import { localizeBatchName, isIndividualSessionsBatch, studentDisplayName } from "../../utils/displayNames";
import { getSession } from "../../api/sessions";
import UserLink from "../common/UserLink"; // Import UserLink
import PageHeaderSection from "../layout/PageHeaderSection";
import ListPageLayout, { ListPageHeader, ListLoadingSpinner } from "../layout/ListPageLayout";
import API from "../../api";
import CarryForwardCell from "./CarryForwardCell";
import { isPeriodBillingMode } from "../../utils/periodBillingCopy";

const withHash = (name, id) => (name ? `${name} (#${id})` : `#${id}`);

/** Debounce for live search */
const useDebounced = (value, delay = 200) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
};

/**
 * Read-only view of a session's attendance.
 * Shows session header + roster + attendance status for all students.
 */
const AttendanceView = ({ role }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    const sessionId =
        location.state?.sessionId ??
        Number(params?.sessionId ?? params?.id ?? 0);

    const [batchId, setBatchId] = useState(
        location.state?.batchId ??
        Number(params?.batchId ?? params?.batch_id ?? 0)
    );

    const [meta, setMeta] = useState(() => ({
        levelName:
            location.state?.levelName ??
            location.state?.level?.name ??
            "",
        batchName:
            location.state?.batchName ??
            location.state?.batch?.name ??
            "",
        coachFullName:
            location.state?.coachFullName ??
            location.state?.coach?.coachFullName ??
            location.state?.coach?.name ??
            "",
        coachId:
            location.state?.coachId ??
            location.state?.coach?.coachId ??
            "",
        status:
            location.state?.status ?? "",
        startTime:
            location.state?.startTime ?? "",
        endTime:
            location.state?.endTime ?? "",
        sessionNotes:
            location.state?.sessionNotes ?? "",
        batchCarryForwardMode:
            location.state?.batchCarryForwardMode ?? null,
        batchChargesMode:
            location.state?.batchChargesMode ?? null,
    }));

    const [rows, setRows] = useState({ regular: [], walkIn: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [softLoadHint, setSoftLoadHint] = useState("");
    const [query, setQuery] = useState("");
    const dq = useDebounced(query, 200);

    const attendanceStatusLabel = (type) => {
        if (type === "PRESENT") return t("attendance.present");
        if (type === "ABSENT") return t("attendance.absent");
        if (!type) return t("attendance.notMarked", { defaultValue: "Not marked" });
        return type || "—";
    };

    const formatSessionTime = (start, end) => {
        if (!start || !end) return "—";
        const s = new Date(start);
        const e = new Date(end);
        if (isNaN(s) || isNaN(e)) {
            return `${formatDateTimeRegional(start)} — ${formatDateTimeRegional(end)}`;
        }
        const sameDay =
            s.getFullYear() === e.getFullYear() &&
            s.getMonth() === e.getMonth() &&
            s.getDate() === e.getDate();
        if (sameDay) {
            return `${formatDate(s)}, ${formatTime(s)} — ${formatTime(e)}`;
        }
        return `${formatDateTimeRegional(start)} — ${formatDateTimeRegional(end)}`;
    };

    const formatWithAMPM = (value) => (value ? formatDateTimeRegional(value) : "");

    // Fallback: fetch full session when header is incomplete
    useEffect(() => {
        let alive = true;
        const needsFetch =
            !!sessionId &&
            (!meta.levelName ||
                !meta.batchName ||
                !meta.coachFullName ||
                !meta.status ||
                !meta.startTime ||
                !meta.endTime ||
                !meta.batchCarryForwardMode ||
                !batchId);

        if (!needsFetch) return;

        (async () => {
            try {
                const res = await getSession(role, sessionId);
                const d = res?.data ?? {};
                if (!alive) return;

                setMeta((prev) => ({
                    ...prev,
                    levelName: prev.levelName || d.levelName || d.level?.name || "",
                    batchName: prev.batchName || d.batchName || d.batch?.name || "",
                    coachFullName:
                        prev.coachFullName ||
                        d.coachFullName ||
                        d.coach?.coachFullName ||
                        d.coach?.name ||
                        "",
                    coachId: prev.coachId || d.coachId || d.coach?.id || "",
                    status: prev.status || d.status || "",
                    startTime: prev.startTime || d.startTime || "",
                    endTime: prev.endTime || d.endTime || "",
                    sessionNotes: prev.sessionNotes || d.sessionNotes || "",
                    batchCarryForwardMode: d.batchCarryForwardMode || null,
                    batchChargesMode: d.batchChargesMode || d.batch?.chargesMode || null,
                }));
                if (d.batchId) {
                    setBatchId((prev) => prev || Number(d.batchId));
                }
            } catch (e) {
                console.warn("AttendanceView getSession failed", e);
            }
        })();

        return () => {
            alive = false;
        };
    }, [sessionId, role, meta.levelName, meta.batchName, meta.coachFullName, meta.status, meta.startTime, meta.endTime, meta.sessionNotes]);

    // Load attendance + enrollments for read-only view
    useEffect(() => {
        if (!sessionId) {
            setError(t("attendance.missingSession"));
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setSoftLoadHint("");
            try {
                const results = await Promise.allSettled([
                    attendanceBySession(role, sessionId),
                    batchId ? listEnrollments(role, { batchId, page: 0, size: 500 }) : Promise.resolve({ data: [] }),
                    getWalkInsBySession(role, sessionId),
                    getSession(role, sessionId),
                ]);
                const attRes = results[0].status === 'fulfilled' ? results[0].value : null;
                const enrRes = results[1].status === 'fulfilled' ? results[1].value : { data: [] };
                const walkInRes = results[2].status === 'fulfilled' ? results[2].value : { data: [] };
                const sessionRes = results[3].status === 'fulfilled' ? results[3].value : { data: {} };
                if (results[0].status === 'rejected') {
                    throw results[0].reason;
                }
                const softFailed = results[1].status === 'rejected' || results[2].status === 'rejected' || results[3].status === 'rejected';
                if (softFailed) {
                    setSoftLoadHint(t('attendance.partialDetailsHint', {
                        defaultValue: 'Some attendance details could not be fully loaded. Roster may be incomplete — refresh to try again.',
                    }));
                }

                const resolvedBatchId = Number(sessionRes?.data?.batchId || batchId || 0);
                if (resolvedBatchId && resolvedBatchId !== Number(batchId || 0)) {
                    setBatchId(resolvedBatchId);
                }

                const sessionStartTime =
                    sessionRes?.data?.startTime ||
                    location.state?.startTime ||
                    "";
                const walkInParticipants = Array.isArray(walkInRes?.data) ? walkInRes.data : [];
                const participantByStudentId = new Map(
                    walkInParticipants.map((w) => [w.studentId, w])
                );

                if (cancelled) return;

                const existingRaw = attRes?.data ?? [];
                const existing = existingRaw.map((a) => ({
                    studentId: a.studentId,
                    attendanceType: a.attendanceType,
                    startTime: a.checkInTime ?? a.startTime,
                    endTime: a.checkOutTime ?? a.endTime,
                    studentName: a.studentFullName || a.studentName,
                    overrideCarryForward: a.overrideCarryForward,
                    studentFullName: a.studentFullName || a.studentName,
                }));

                // Try to get session-specific enrollments first (for walk-in sessions)
                let enrollmentsRaw = [];
                if (sessionId) {
                    try {
                        const baseRole = role === "ADMIN" ? "admin" : role === "MANAGER" ? "manager" : "coach";
                        const sessionEnrRes = await API.get(`/${baseRole}/sessions/${sessionId}/enrollments`);
                        const sessionEnr = Array.isArray(sessionEnrRes?.data) ? sessionEnrRes.data : sessionEnrRes?.data?.content || [];

                        if (sessionEnr.length > 0) {
                            enrollmentsRaw = sessionEnr;
                        }
                    } catch (err) {
                        console.log("Session-specific enrollments not available, using batch enrollments", err);
                    }
                }

                // Fallback to batch enrollments if session-specific didn't work
                if (enrollmentsRaw.length === 0 && resolvedBatchId) {
                    if (Number(batchId) === resolvedBatchId) {
                        enrollmentsRaw = Array.isArray(enrRes?.data?.content) ? enrRes.data.content : enrRes?.data ?? [];
                    } else {
                        try {
                            const freshEnr = await listEnrollments(role, { batchId: resolvedBatchId, page: 0, size: 500 });
                            enrollmentsRaw = Array.isArray(freshEnr?.data?.content)
                                ? freshEnr.data.content
                                : (freshEnr?.data ?? []);
                        } catch (err) {
                            console.log("Batch enrollments fallback failed", err);
                        }
                    }
                }

                const enrollmentByStudentId = new Map();
                for (const e of enrollmentsRaw) {
                    if (!e.studentId) continue;
                    enrollmentByStudentId.set(e.studentId, e);
                }

                const byStudent = new Map();
                for (const a of existing) {
                    byStudent.set(a.studentId, a);
                }

                const regularStudents = [];
                const walkInStudents = [];

                if (enrollmentByStudentId.size > 0) {
                    for (const [sid, enr] of enrollmentByStudentId.entries()) {
                        const existingRow = byStudent.get(sid);
                        const studentRow = {
                            studentId: sid,
                            attendanceType: existingRow?.attendanceType || "",
                            startTime: existingRow?.startTime || "",
                            endTime: existingRow?.endTime || "",
                            studentName: enr.studentFullName || enr.studentName,
                            overrideCarryForward: enr.overrideCarryForward,
                            studentFullName: enr.studentFullName || enr.studentName,
                            isWalkIn: enr.isWalkIn || false,
                        };

                        if (enr.isWalkIn) {
                            const participant = participantByStudentId.get(sid);
                            if (!studentRow.startTime && participant?.checkinTime) {
                                studentRow.startTime =
                                    combineSessionDateWithLocalTime(sessionStartTime, participant.checkinTime) || "";
                            }
                            if (!studentRow.endTime && participant?.checkoutTime) {
                                studentRow.endTime =
                                    combineSessionDateWithLocalTime(sessionStartTime, participant.checkoutTime) || "";
                            }
                            walkInStudents.push(studentRow);
                        } else {
                            regularStudents.push(studentRow);
                        }
                        byStudent.delete(sid);
                    }
                } else {
                    for (const a of existing) {
                        regularStudents.push({
                            ...a,
                            isWalkIn: false,
                        });
                        byStudent.delete(a.studentId);
                    }
                }

                // Any remaining attendance-only students (no enrollment record)
                for (const [sid, a] of byStudent.entries()) {
                    regularStudents.push({
                        ...a,
                        studentName: a.studentFullName || a.studentName || studentDisplayName(null, sid, t),
                        studentFullName: a.studentFullName || a.studentName || studentDisplayName(null, sid, t),
                        isWalkIn: false,
                    });
                }

                // Walk-ins from session participants not already in the walk-in table
                for (const participant of walkInParticipants) {
                    const sid = participant.studentId;
                    if (!sid || walkInStudents.some((w) => w.studentId === sid)) continue;

                    const existingRow = byStudent.get(sid);
                    walkInStudents.push({
                        studentId: sid,
                        attendanceType: existingRow?.attendanceType || "",
                        startTime:
                            existingRow?.startTime ||
                            combineSessionDateWithLocalTime(sessionStartTime, participant.checkinTime) ||
                            "",
                        endTime:
                            existingRow?.endTime ||
                            combineSessionDateWithLocalTime(sessionStartTime, participant.checkoutTime) ||
                            "",
                        studentName: participant.studentName,
                        studentFullName: participant.studentName,
                        isWalkIn: true,
                    });
                }

                // Store both regular and walk-in students separately
                setRows({ regular: regularStudents, walkIn: walkInStudents });
            } catch (e) {
                console.error(e);
                setRows({ regular: [], walkIn: [] });
                setError(t("attendance.loadDetailsFailed"));
            } finally {
                setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [sessionId, batchId, role, t]);

    const tokens = useMemo(
        () =>
            dq
                .toLowerCase()
                .split(/\s+/)
                .map((t) => t.trim())
                .filter(Boolean),
        [dq]
    );

    const filteredRegular = useMemo(() => {
        if (!tokens.length) return rows.regular;
        return rows.regular.filter((r) => {
            const hay = `${(r.studentFullName || r.studentName || "")} #${r.studentId}`.toLowerCase();
            return tokens.every((t) => hay.includes(t));
        });
    }, [rows.regular, tokens]);

    const filteredWalkIn = useMemo(() => {
        if (!tokens.length) return rows.walkIn;
        return rows.walkIn.filter((r) => {
            const hay = `${(r.studentFullName || r.studentName || "")} #${r.studentId}`.toLowerCase();
            return tokens.every((t) => hay.includes(t));
        });
    }, [rows.walkIn, tokens]);

    const startLabel = formatDateTime(meta.startTime);
    const endLabel = formatDateTime(meta.endTime);
    const statusRaw = (meta.status || "").toString().toUpperCase();
    const statusDisplay = statusRaw === "PENDING"
        ? t("sessions.statusPending")
        : statusRaw === "COMPLETED"
            ? t("sessions.statusCompleted")
            : statusRaw === "CANCELLED"
                ? t("sessions.statusCancelled")
                : (meta.status || "—");

    // Check if this is an individual sessions batch (carry forward doesn't apply)
    const isIndividualSession = isIndividualSessionsBatch(meta.batchName);
    const showCarryForward = !isIndividualSession && !isPeriodBillingMode(meta.batchChargesMode);

    if (loading) {
        return (
            <ListPageLayout>
                <ListPageHeader title={t("attendance.viewTitle")} subtitle={t("attendance.viewLoading")} />
                <ListLoadingSpinner />
            </ListPageLayout>
        );
    }

    const batchDisplayName = localizeBatchName(meta.batchName, t);

    return (
        <div className="list-page">
            <PageHeaderSection
                title={t("attendance.viewTitle")}
                subtitle={`${meta.levelName || "—"} • ${batchDisplayName || "—"} — ${formatSessionTime(meta.startTime, meta.endTime)}`}
            >
                <div className="w-full text-sm text-slate-700 space-y-1">
                        <div>
                            <b>{t("attendance.coach")}:</b>{" "}
                            {meta.coachFullName && meta.coachId ? (
                                <UserLink id={meta.coachId} name={meta.coachFullName} />
                            ) : (
                                "—"
                            )}
                        </div>
                        <div>
                            <b>{t("attendance.status")}:</b>{" "}
                            <span>{statusDisplay || "—"}</span>
                        </div>
                    {meta.sessionNotes && (
                        <p className="mt-2 text-sm text-gray-700">
                            <span className="font-semibold">{t("attendance.sessionNotes")}:</span> {meta.sessionNotes}
                        </p>
                    )}
                </div>
            </PageHeaderSection>

            {statusRaw === 'CANCELLED' && (
                <div className="bg-red-50 border border-red-300 rounded p-4">
                    <div className="flex items-start">
                        <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                            <h3 className="font-semibold text-red-800">{t("attendance.sessionCancelled")}</h3>
                            <p className="text-sm text-red-700 mt-1">
                                {t("attendance.cancelledDesc")}
                                {(rows.regular.length > 0 || rows.walkIn.length > 0) && t("attendance.cancelledHistorical")}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-3">
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("attendance.searchStudents")}
                    className="w-full border rounded px-3 py-2 outline-none"
                />
            </div>

            {softLoadHint && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                    {softLoadHint}
                </div>
            )}

            {error && (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {error}
                </div>
            )}

            {/* Regular Batch Students */}
            {filteredRegular.length > 0 && (
                <div className="bg-white rounded shadow p-3 mb-4">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">{t("attendance.batchStudents")}</h3>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm border">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 border text-center">{t("attendance.student")}</th>
                                    <th className="px-3 py-2 border text-center">{t("attendance.status")}</th>
                                    <th className="px-3 py-2 border text-center">{t("attendance.checkIn")}</th>
                                    <th className="px-3 py-2 border text-center">{t("attendance.checkOut")}</th>
                                    {showCarryForward && (
                                        <th className="px-3 py-2 border text-center">{t("attendance.carryForward")}</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRegular.map((r) => {
                                    const bgColor =
                                        r.attendanceType === "PRESENT" ? "bg-green-50" :
                                            r.attendanceType === "ABSENT" ? "bg-red-50" : "bg-white";

                                    const renderCarryForward = () => (
                                        <CarryForwardCell
                                            batchMode={meta.batchCarryForwardMode}
                                            overrideCarryForward={r.overrideCarryForward}
                                        />
                                    );

                                    return (
                                        <tr key={r.studentId} className={`border-t ${bgColor}`}>
                                            <td className="px-3 py-2 text-center">
                                                <UserLink
                                                    id={r.studentId}
                                                    name={r.studentName || r.studentFullName || withHash("", r.studentId)}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className="font-medium">
                                                    {attendanceStatusLabel(r.attendanceType)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-center text-gray-600">
                                                {r.startTime ? formatWithAMPM(r.startTime) : t("attendance.notApplicable")}
                                            </td>
                                            <td className="px-3 py-2 text-center text-gray-600">
                                                {r.endTime ? formatWithAMPM(r.endTime) : t("attendance.notApplicable")}
                                            </td>
                                            {showCarryForward && (
                                                <td className="px-3 py-2 text-center">
                                                    {renderCarryForward()}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Walk-in Students */}
            {filteredWalkIn.length > 0 && (
                <div className="bg-blue-50 rounded shadow p-3 border-2 border-blue-200">
                    <div className="flex items-center mb-3">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-blue-800">{t("attendance.walkInLabel")}</h3>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm border border-blue-200 bg-white">
                            <thead className="bg-blue-100">
                                <tr>
                                    <th className="px-3 py-2 border border-blue-200 text-center">{t("attendance.student")}</th>
                                    <th className="px-3 py-2 border border-blue-200 text-center">{t("attendance.status")}</th>
                                    <th className="px-3 py-2 border border-blue-200 text-center">{t("attendance.checkIn")}</th>
                                    <th className="px-3 py-2 border border-blue-200 text-center">{t("attendance.checkOut")}</th>
                                    <th className="px-3 py-2 border border-blue-200 text-center">{t("attendance.type")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredWalkIn.map((r) => {
                                    const bgColor =
                                        r.attendanceType === "PRESENT" ? "bg-green-50" :
                                            r.attendanceType === "ABSENT" ? "bg-red-50" : "bg-white";

                                    return (
                                        <tr key={r.studentId} className={`border-t border-blue-200 ${bgColor}`}>
                                            <td className="px-3 py-2 text-center">
                                                <UserLink
                                                    id={r.studentId}
                                                    name={r.studentName || r.studentFullName || withHash("", r.studentId)}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className="font-medium">
                                                    {attendanceStatusLabel(r.attendanceType)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-center text-gray-600">
                                                {r.startTime ? formatWithAMPM(r.startTime) : t("attendance.notApplicable")}
                                            </td>
                                            <td className="px-3 py-2 text-center text-gray-600">
                                                {r.endTime ? formatWithAMPM(r.endTime) : t("attendance.notApplicable")}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 font-medium">
                                                    {t("attendance.walkInType")}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* No students found */}
            {!error && filteredRegular.length === 0 && filteredWalkIn.length === 0 && (
                <div className="bg-white rounded shadow p-3">
                    <div className="px-3 py-6 text-center text-gray-500">
                        {t("attendance.noStudentsMatch", { query: dq })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttendanceView;
